/**
 * Safe number formatters for report data.
 *
 * Backend data can contain null or undefined values (e.g. missing metrics,
 * incomplete periods). These formatters return a fallback string instead
 * of crashing on null.toFixed().
 */

const MISSING = '-';

/**
 * How a metric should be rendered. The backend sends RAW floats (e.g. volatility
 * 0.1988, sharpe 0.6); the FE owns the display: `percent` ×100 + "%", `multiple`
 * appends "×", `ratio`/`int` are plain numbers, `date`/strings pass through.
 */
export type MetricFormat = 'percent' | 'ratio' | 'multiple' | 'int' | 'date';

/**
 * Format a metric value for display. With an explicit `format` the raw float is
 * rendered accordingly; without one it falls back to a value-shape heuristic
 * (kept for cards not yet migrated to explicit formats).
 */
export function formatMetricValue(
  value: number | string | null | undefined,
  format?: MetricFormat,
): string {
  if (value == null) return MISSING;
  if (typeof value === 'string') return value; // dates / pre-formatted text
  switch (format) {
    case 'percent':
      return `${(value * 100).toFixed(2)}%`;
    case 'multiple':
      return `${value.toFixed(2)}×`;
    case 'int':
      return String(Math.round(value));
    case 'ratio':
      return value.toFixed(2);
    default:
      if (Number.isInteger(value)) return String(value);
      return value.toFixed(2);
  }
}

/** Format a number with fixed decimals. Returns fallback for null/undefined. */
export function formatFixed(value: number | null | undefined, decimals: number, fallback = MISSING): string {
  if (value == null) return fallback;
  return value.toFixed(decimals);
}

/** Format a signed percentage: "+3.2%" / "-1.5%" / "-" for null. */
export function formatPercent(value: number | null | undefined, decimals = 1, fallback = MISSING): string {
  if (value == null) return fallback;
  const sign = value >= 0 ? '+' : '';
  return `${sign}${value.toFixed(decimals)}%`;
}
